import { useCallback, useEffect, useRef, useState } from 'react';
import { useSynthesizeSpeech } from '@/hooks/usePerformance';
import {
  createAudioPlayback,
  speakWithBrowser,
  splitIntoSpeechChunks,
  stopBrowserSpeech,
  stripMarkdownForSpeech,
  type AudioPlayback,
} from '@/lib/speech-utils';

/**
 * Respuestas habladas de los agentes (D-Talk / Mapa de Talento).
 *
 * speak(): markdown → texto → chunks ≤2500 chars → Polly secuencial con
 * prefetch del chunk siguiente mientras suena el actual (sin gaps). Si Polly
 * falla, cae al TTS del navegador. stop() corta al instante (generation
 * counter: cualquier speak/stop posterior invalida lo que estaba en vuelo).
 */
export function useSpokenReply() {
  const synthesize = useSynthesizeSpeech();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const genRef = useRef(0);
  const playbackRef = useRef<AudioPlayback | null>(null);

  const stop = useCallback(() => {
    genRef.current += 1;
    playbackRef.current?.stop();
    playbackRef.current = null;
    stopBrowserSpeech();
    setIsSpeaking(false);
  }, []);

  useEffect(() => stop, [stop]); // cortar al desmontar

  const speak = useCallback(
    async (markdown: string, opts: { voice: string; browserLang: string }) => {
      stop();
      const gen = genRef.current;
      const text = stripMarkdownForSpeech(markdown);
      if (!text) return;
      const chunks = splitIntoSpeechChunks(text);
      setIsSpeaking(true);
      try {
        let next = synthesize.mutateAsync({ text: chunks[0], voice: opts.voice });
        for (let i = 0; i < chunks.length; i++) {
          const current = await next;
          if (genRef.current !== gen) return;
          if (i + 1 < chunks.length) {
            next = synthesize.mutateAsync({ text: chunks[i + 1], voice: opts.voice });
          }
          const playback = createAudioPlayback(current.audio, current.mimeType);
          playbackRef.current = playback;
          await playback.done;
          if (genRef.current !== gen) return;
        }
      } catch {
        // Polly falló: leer lo que queda con el TTS del navegador
        if (genRef.current === gen) speakWithBrowser(text, opts.browserLang);
      } finally {
        if (genRef.current === gen) {
          playbackRef.current = null;
          setIsSpeaking(false);
        }
      }
    },
    [stop, synthesize]
  );

  return { speak, stop, isSpeaking };
}
